import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  TrendingUp as ConversionIcon,
  CardGiftcard as GuestPassIcon,
  ToggleOn as ActiveIcon,
  ToggleOff as InactiveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FamilyRestroom as FamilyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import nonMemberService from '../api/services/nonMemberService';
import nonMemberFamilyService from '../api/services/nonMemberFamilyService';
import adminAnalyticsService from '../api/services/adminAnalyticsService';
import { NonMember, GuestPass, NonMemberFamily, NonMemberFamilyFormData } from '../types';

const NonMemberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [nonMember, setNonMember] = useState<NonMember | null>(null);
  const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
  const [guestVisitStatus, setGuestVisitStatus] = useState<{
    visits_this_year: number;
    annual_limit: number;
    remaining_visits: number;
    reset_date: string | null;
  } | null>(null);
  const [familyMembers, setFamilyMembers] = useState<NonMemberFamily[]>([]);
  const [familyDialogOpen, setFamilyDialogOpen] = useState(false);
  const [familyBusy, setFamilyBusy] = useState(false);
  // null = adding a new family member; a number = editing that existing row.
  const [editingFamilyId, setEditingFamilyId] = useState<number | null>(null);
  const [familyForm, setFamilyForm] = useState<NonMemberFamilyFormData>({
    first_name: '', last_name: '', date_of_birth: '', relationship_type: 'CHILD',
    member_sub_number: '', emergency_contact_name: '', emergency_contact_phone: '',
  });

  // Dialog states
  const [interestDialogOpen, setInterestDialogOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [newInterestLevel, setNewInterestLevel] = useState('none');
  const [interestNotes, setInterestNotes] = useState('');
  const [blockReason, setBlockReason] = useState('');

  const loadNonMember = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const [memberResponse, passesResponse, visitResponse, familyResponse] = await Promise.all([
        nonMemberService.getNonMemberById(id),
        nonMemberService.getGuestPasses(id),
        nonMemberService.getGuestVisitStatus(id),
        nonMemberFamilyService.getByNonMember(id),
      ]);

      setNonMember(memberResponse.data);
      setGuestPasses(passesResponse.data || []);
      setGuestVisitStatus(visitResponse.data);
      setFamilyMembers(familyResponse.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load non-member details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadNonMember();
  }, [loadNonMember]);

  const handleUpdateInterest = async () => {
    if (!nonMember) return;

    try {
      setError(null);
      await adminAnalyticsService.updateInterestLevel(nonMember.id, newInterestLevel, interestNotes || undefined);
      setSuccess('Interest level updated successfully');
      setInterestDialogOpen(false);
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update interest level');
    }
  };

  const handleToggleStatus = async () => {
    if (!nonMember) return;

    try {
      setError(null);
      const currentStatus = nonMember.membership_status?.toUpperCase();
      const newStatus = currentStatus === 'NON_MEMBER_ACTIVE' ? 'NON_MEMBER_INACTIVE' : 'NON_MEMBER_ACTIVE';

      await adminAnalyticsService.updateStatus(nonMember.id, newStatus as 'NON_MEMBER_ACTIVE' | 'NON_MEMBER_INACTIVE');
      setSuccess(`Status updated to ${newStatus.replace('NON_MEMBER_', '').toLowerCase()}`);
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleBlock = async () => {
    if (!nonMember) return;

    try {
      setError(null);
      await adminAnalyticsService.blockNonMember(nonMember.id, blockReason || undefined);
      setSuccess('Non-member blocked successfully');
      setBlockDialogOpen(false);
      setBlockReason('');
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to block non-member');
    }
  };

  const handleUnblock = async () => {
    if (!nonMember) return;

    try {
      setError(null);
      await adminAnalyticsService.unblockNonMember(nonMember.id);
      setSuccess('Non-member unblocked successfully');
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unblock non-member');
    }
  };

  const handleResetGuestVisits = async () => {
    if (!nonMember) return;

    try {
      setError(null);
      await adminAnalyticsService.resetGuestVisitsForMember(nonMember.id);
      setSuccess('Guest visits reset successfully');
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset guest visits');
    }
  };

  const emptyFamilyForm: NonMemberFamilyFormData = {
    first_name: '', last_name: '', date_of_birth: '', relationship_type: 'CHILD',
    member_sub_number: '', emergency_contact_name: '', emergency_contact_phone: '',
  };

  const openAddFamily = () => {
    setEditingFamilyId(null);
    setFamilyForm({ ...emptyFamilyForm });
    setFamilyDialogOpen(true);
  };

  const openEditFamily = (fm: NonMemberFamily) => {
    setEditingFamilyId(fm.id);
    setFamilyForm({
      first_name: fm.first_name || '',
      last_name: fm.last_name || '',
      date_of_birth: fm.date_of_birth || '',
      relationship_type: fm.relationship_type || 'CHILD',
      member_sub_number: fm.member_sub_number || '',
      emergency_contact_name: fm.emergency_contact_name || '',
      emergency_contact_phone: fm.emergency_contact_phone || '',
    });
    setFamilyDialogOpen(true);
  };

  const handleSaveFamily = async () => {
    if (!nonMember) return;
    if (!familyForm.first_name.trim() || !familyForm.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    // Account number (member_sub_number) is only sent when editing — it auto-assigns
    // on create, and only staff (this page) may override it afterward.
    try {
      setFamilyBusy(true);
      setError(null);
      if (editingFamilyId != null) {
        await nonMemberFamilyService.update(nonMember.id, editingFamilyId, {
          first_name: familyForm.first_name.trim(),
          last_name: familyForm.last_name.trim(),
          date_of_birth: familyForm.date_of_birth || undefined,
          relationship_type: familyForm.relationship_type,
          member_sub_number: familyForm.member_sub_number?.trim() || '',
          emergency_contact_name: familyForm.emergency_contact_name?.trim() || undefined,
          emergency_contact_phone: familyForm.emergency_contact_phone?.trim() || undefined,
        });
        setSuccess('Family member updated');
      } else {
        await nonMemberFamilyService.create(nonMember.id, {
          first_name: familyForm.first_name.trim(),
          last_name: familyForm.last_name.trim(),
          date_of_birth: familyForm.date_of_birth || undefined,
          relationship_type: familyForm.relationship_type,
          emergency_contact_name: familyForm.emergency_contact_name?.trim() || undefined,
          emergency_contact_phone: familyForm.emergency_contact_phone?.trim() || undefined,
        });
        setSuccess('Family member added');
      }
      setFamilyDialogOpen(false);
      setEditingFamilyId(null);
      setFamilyForm({ ...emptyFamilyForm });
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error
        || (editingFamilyId != null ? 'Failed to update family member' : 'Failed to add family member'));
    } finally {
      setFamilyBusy(false);
    }
  };

  const handleDeleteFamily = async (familyId: number, name: string) => {
    if (!nonMember) return;
    if (!window.confirm(`Remove ${name} from this non-member's family?`)) return;
    try {
      setError(null);
      await nonMemberFamilyService.delete(nonMember.id, familyId);
      setSuccess('Family member removed');
      loadNonMember();
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to remove family member');
    }
  };

  const getStatusColor = (status: string | undefined): 'success' | 'warning' | 'error' | 'default' => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'NON_MEMBER_ACTIVE':
        return 'success';
      case 'NON_MEMBER_INACTIVE':
        return 'warning';
      case 'NON_MEMBER_BLOCKED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getInterestColor = (level: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (level) {
      case 'high':
      case 'ready_to_apply':
        return 'success';
      case 'medium':
        return 'primary';
      case 'low':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPassStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
    const upperStatus = status?.toUpperCase();
    switch (upperStatus) {
      case 'COMPLETED':
        return 'success';
      case 'ACTIVE':
        return 'primary';
      case 'ISSUED':
        return 'warning';
      case 'CANCELLED':
      case 'NO_SHOW':
      case 'EXPIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDateTime = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!nonMember) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Alert severity="error">Non-member not found</Alert>
          <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Box>
      </Container>
    );
  }

  const isBlocked = nonMember.membership_status?.toUpperCase() === 'NON_MEMBER_BLOCKED';
  const isActive = nonMember.membership_status?.toUpperCase() === 'NON_MEMBER_ACTIVE';

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
            Non-Member Details
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadNonMember}
          >
            Refresh
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Profile Card */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Box display="flex" flexDirection="column" alignItems="center" mb={2}>
                  <Avatar
                    sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}
                    src={nonMember.profile_photo_url}
                  >
                    {nonMember.first_name?.[0]}{nonMember.last_name?.[0]}
                  </Avatar>
                  <Typography variant="h5">
                    {nonMember.first_name} {nonMember.last_name}
                  </Typography>
                  <Chip
                    label={nonMember.membership_status?.replace('NON_MEMBER_', '').replace('_', ' ') || 'Unknown'}
                    color={getStatusColor(nonMember.membership_status)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ '& > div': { display: 'flex', alignItems: 'center', mb: 1.5 } }}>
                  <Box>
                    <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                    <Typography variant="body2">{nonMember.email}</Typography>
                  </Box>
                  {nonMember.phone && (
                    <Box>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                      <Typography variant="body2">{nonMember.phone}</Typography>
                    </Box>
                  )}
                  {(nonMember.city || nonMember.state) && (
                    <Box>
                      <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                      <Typography variant="body2">
                        {[nonMember.city, nonMember.state].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} fontSize="small" />
                    <Typography variant="body2">
                      Joined: {formatDate(nonMember.created_at)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Actions */}
                <Box display="flex" flexDirection="column" gap={1}>
                  {!isBlocked && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color={isActive ? 'warning' : 'success'}
                      startIcon={isActive ? <InactiveIcon /> : <ActiveIcon />}
                      onClick={handleToggleStatus}
                    >
                      {isActive ? 'Set Inactive' : 'Set Active'}
                    </Button>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ConversionIcon />}
                    onClick={() => {
                      setNewInterestLevel(nonMember.conversion_interest_level || 'none');
                      setInterestNotes(nonMember.conversion_notes || '');
                      setInterestDialogOpen(true);
                    }}
                  >
                    Update Interest
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleResetGuestVisits}
                    disabled={(guestVisitStatus?.visits_this_year || 0) === 0}
                  >
                    Reset Guest Visits
                  </Button>
                  {isBlocked ? (
                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleUnblock}
                    >
                      Unblock
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => setBlockDialogOpen(true)}
                    >
                      Block
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Details Column */}
          <Grid size={{ xs: 12, md: 8 }}>
            {/* Guest Visit Stats */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <GuestPassIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Guest Visit Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="h4" color="primary">
                        {guestVisitStatus?.visits_this_year || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visits This Year
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="h4" color="secondary">
                        {guestVisitStatus?.annual_limit || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Annual Limit
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                      <Typography
                        variant="h4"
                        color={(guestVisitStatus?.remaining_visits || 0) <= 1 ? 'error' : 'success'}
                      >
                        {guestVisitStatus?.remaining_visits || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Remaining
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <Box textAlign="center" p={2} bgcolor="grey.100" borderRadius={1}>
                      <Typography variant="body1" color="text.primary">
                        {guestVisitStatus?.reset_date ? formatDate(guestVisitStatus.reset_date) : '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Last Reset
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Conversion Interest */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ConversionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Conversion Interest
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Interest Level
                    </Typography>
                    <Chip
                      label={nonMember.conversion_interest_level || 'none'}
                      color={getInterestColor(nonMember.conversion_interest_level || '')}
                      sx={{ mt: 0.5 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Acquisition Source
                    </Typography>
                    <Typography variant="body1">
                      {nonMember.acquisition_source || '-'}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Acquisition Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(nonMember.acquisition_date)}
                    </Typography>
                  </Grid>
                  {nonMember.conversion_notes && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {nonMember.conversion_notes}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Family Members */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="h6">
                    <FamilyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Family Members ({familyMembers.length})
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={openAddFamily}>
                    Add
                  </Button>
                </Box>
                {familyMembers.length === 0 ? (
                  <Typography color="text.secondary">
                    No family members yet. Add children/dependents here so they can be enrolled in swim, lessons, or kids club.
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Account #</TableCell>
                          <TableCell>Relationship</TableCell>
                          <TableCell>Date of Birth</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {familyMembers.map((fm) => (
                          <TableRow key={fm.id}>
                            <TableCell>{fm.first_name} {fm.last_name}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                              {fm.member_sub_number || '—'}
                            </TableCell>
                            <TableCell sx={{ textTransform: 'capitalize' }}>
                              {String(fm.relationship_type).toLowerCase()}
                            </TableCell>
                            <TableCell>
                              {formatDate(fm.date_of_birth)}{fm.age != null ? ` (${fm.age})` : ''}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => openEditFamily(fm)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteFamily(fm.id, `${fm.first_name} ${fm.last_name}`)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>

            {/* Guest Pass History */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <GuestPassIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Guest Pass History ({guestPasses.length})
                </Typography>
                {guestPasses.length === 0 ? (
                  <Typography color="text.secondary">No guest passes found</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pass Code</TableCell>
                          <TableCell>Visit Date</TableCell>
                          <TableCell>Sponsor</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Check-In</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {guestPasses.map((pass) => (
                          <TableRow key={pass.id}>
                            <TableCell>{pass.pass_code}</TableCell>
                            <TableCell>{formatDate(pass.visit_date)}</TableCell>
                            <TableCell>
                              {pass.sponsor
                                ? `${pass.sponsor.first_name} ${pass.sponsor.last_name}`
                                : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={pass.status}
                                color={getPassStatusColor(pass.status as string)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {pass.check_in_time ? formatDateTime(pass.check_in_time) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Update Interest Dialog */}
        <Dialog open={interestDialogOpen} onClose={() => setInterestDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Update Conversion Interest</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Interest Level</InputLabel>
              <Select
                value={newInterestLevel}
                label="Interest Level"
                onChange={(e) => setNewInterestLevel(e.target.value)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="ready_to_apply">Ready to Apply</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={interestNotes}
              onChange={(e) => setInterestNotes(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInterestDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateInterest}>
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Block Dialog */}
        <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Block Non-Member</DialogTitle>
          <DialogContent>
            <Typography gutterBottom>
              Are you sure you want to block {nonMember.first_name} {nonMember.last_name}?
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Reason (optional)"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBlockDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" color="error" onClick={handleBlock}>
              Block
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Family Member Dialog */}
        <Dialog
          open={familyDialogOpen}
          onClose={() => { if (!familyBusy) { setFamilyDialogOpen(false); setEditingFamilyId(null); } }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingFamilyId != null ? 'Edit Family Member' : 'Add Family Member'}</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
              {/* Relationship first to mirror AddFamilyMemberDialog (member side)
                  so the two dialogs read top-to-bottom in the same order. */}
              <FormControl fullWidth>
                <InputLabel>Relationship</InputLabel>
                <Select
                  value={familyForm.relationship_type}
                  label="Relationship"
                  onChange={(e) => setFamilyForm({ ...familyForm, relationship_type: e.target.value })}
                >
                  <MenuItem value="CHILD">Child</MenuItem>
                  <MenuItem value="SPOUSE">Spouse</MenuItem>
                  <MenuItem value="PARTNER">Partner</MenuItem>
                  <MenuItem value="PARENT">Parent</MenuItem>
                  <MenuItem value="SIBLING">Sibling</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  required
                  label="First Name"
                  value={familyForm.first_name}
                  onChange={(e) => setFamilyForm({ ...familyForm, first_name: e.target.value })}
                />
                <TextField
                  fullWidth
                  required
                  label="Last Name"
                  value={familyForm.last_name}
                  onChange={(e) => setFamilyForm({ ...familyForm, last_name: e.target.value })}
                />
              </Box>
              <TextField
                label="Date of Birth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={familyForm.date_of_birth || ''}
                onChange={(e) => setFamilyForm({ ...familyForm, date_of_birth: e.target.value })}
              />
              {/* Account number — staff-only override. Auto-assigned on create
                  ({primary}-DPnn), so we only expose it when editing an existing
                  family member. Leave blank to clear it. */}
              {editingFamilyId != null && (
                <TextField
                  label="Account Number"
                  value={familyForm.member_sub_number || ''}
                  onChange={(e) => setFamilyForm({ ...familyForm, member_sub_number: e.target.value })}
                  helperText="Auto-assigned from the primary's number (e.g. NM00012-DP01). Staff override only — leave blank to clear."
                  sx={{ '& input': { fontFamily: 'monospace' } }}
                />
              )}
              {/* Emergency contact (optional at create — backend stores them on
                  the NonMemberFamily row; reachable in the contacts popup later). */}
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">Emergency contact (optional)</Typography>
              </Divider>
              <Box display="flex" gap={2}>
                <TextField
                  fullWidth
                  label="Contact name"
                  value={familyForm.emergency_contact_name || ''}
                  onChange={(e) => setFamilyForm({ ...familyForm, emergency_contact_name: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Contact phone"
                  placeholder="(555) 123-4567"
                  value={familyForm.emergency_contact_phone || ''}
                  onChange={(e) => setFamilyForm({ ...familyForm, emergency_contact_phone: e.target.value })}
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => { setFamilyDialogOpen(false); setEditingFamilyId(null); }}
              disabled={familyBusy}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveFamily}
              disabled={familyBusy || !familyForm.first_name.trim() || !familyForm.last_name.trim()}
            >
              {familyBusy
                ? (editingFamilyId != null ? 'Saving…' : 'Adding…')
                : (editingFamilyId != null ? 'Save' : 'Add')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default NonMemberDetailPage;
